/**
 * Snippet Category Constants and Helpers
 *
 * Defines the available category filters for snippets and provides
 * internationalized category labels. Categories are used by the
 * SnippetTabFilters component and the useFilteredSnippets hook.
 *
 * Category filtering logic:
 * - ALL: Shows all snippets (no filtering)
 * - MY_TEMPLATES: Shows only snippets created by the current user
 * - FAVORITES: Shows only snippets marked as favorites
 */
import { defineMessages, useIntl } from 'react-intl-next';

import type { Category } from '@confluence/category-selector';

/** Constant IDs for snippet category filters - used as keys in state and filter logic */
export enum SNIPPET_CATEGORY_IDS {
	ALL = 'snippets.all',
	MY_TEMPLATES = 'snippets.my-templates',
	FAVORITES = 'snippets.favorites',
}

/** The default category shown when the filter UI first loads */
export const DEFAULT_CATEGORY_ID = SNIPPET_CATEGORY_IDS.ALL;

export const snippetCategoryI18n = defineMessages({
	allCategory: {
		id: 'snippets.snippet-tab.category.all',
		defaultMessage: 'All',
		description: 'Label for the All category filter in snippet templates',
	},
	myTemplatesCategory: {
		id: 'snippets.snippet-tab.category.my-templates',
		defaultMessage: 'My Templates',
		description: 'Label for the My Templates category filter in snippet templates',
	},
	favoritesCategory: {
		id: 'snippets.snippet-tab.category.favorites',
		defaultMessage: 'Favorites',
		description: 'Label for the Favorites category filter in snippet templates',
	},
});

/**
 * Hook that returns the categories array for snippet filters.
 * @returns Array of Category objects
 */
export const useSnippetFilterCategories = (): Category[] => {
	const intl = useIntl();

	return [
		{ id: SNIPPET_CATEGORY_IDS.ALL, name: intl.formatMessage(snippetCategoryI18n.allCategory) },
		{
			id: SNIPPET_CATEGORY_IDS.MY_TEMPLATES,
			name: intl.formatMessage(snippetCategoryI18n.myTemplatesCategory),
		},
		{
			id: SNIPPET_CATEGORY_IDS.FAVORITES,
			name: intl.formatMessage(snippetCategoryI18n.favoritesCategory),
		},
	];
};
